import { CATEGORY_ICONS, fmt, fmtQty, type CartLine, type Product } from '@k2pos/core';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar } from '@/components/ui/AppBar';
import { Card } from '@/components/ui/Card';
import { CategoryPill } from '@/components/ui/Chip';
import { QtySheet } from '@/components/ui/QtySheet';
import { Mono, Text } from '@/components/ui/Text';
import { CategoryGlyph, IconClose, IconSearch } from '@/components/icons';
import { can, on, post } from '@/bridge';
import { useProductImages } from '@/hooks/useProductImages';
import { cx } from '@/lib/cx';
import { cartTotal, usePos } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './Sell.module.css';

const ALL = 'ທັງໝົດ';

/** Build the cart from the catalog. Search-by-name is the primary path — sand,
 *  gravel and steel have no barcode to scan. */
export function Sell() {
  const navigate = useNavigate();
  const catalog = usePos((s) => s.catalog);
  const cart = usePos((s) => s.cart);
  const qtyOf = usePos((s) => s.qtyOf);
  const linesOf = usePos((s) => s.linesOf);
  const selectUnit = usePos((s) => s.selectUnit);

  /**
   * Subscribed to as state, not read through `s.selectedUnit`.
   *
   * That getter is a stable function reference, so selecting it never marks the
   * screen dirty: switching ຖົງ to ໂຕນ inside the quantity sheet changed the
   * store but re-rendered nothing, leaving the sheet holding a stale unit and a
   * stale draft. Typing 0.5 after 12 then produced 120.5 ໂຕນ. The RN screen has
   * the same latent bug; do not "simplify" this back to the getter.
   */
  const unitSel = usePos((s) => s.unitSel);
  const selectedUnit = (productId: string) => unitSel[productId] ?? 0;

  const setQty = usePos((s) => s.setQty);

  const [query, setQuery] = useState('');
  const [cat, setCat] = useState(ALL);
  // Fetched once per change, not carried in the state blob. See the hook.
  const images = useProductImages();

  /**
   * Only offered when the host can actually scan.
   *
   * There was a ສະແກນ button here always, and in a browser — which is every
   * install today, the shell advertises no capabilities — it quietly cleared
   * the search box instead. A button that says it does one thing and does
   * another is worse than a dead one, and worse again than no button.
   */
  const scannable = can('scan');
  useEffect(() => {
    if (!scannable) return;
    return on('scan.result', (message) => {
      const text = (message.payload as { text?: string } | undefined)?.text;
      if (text) setQuery(text);
    });
  }, [scannable]);
  /** Product whose quantity sheet is open. */
  const [sheetId, setSheetId] = useState<string | null>(null);

  const categories = useMemo(() => {
    const counts = new Map<string, number>([[ALL, catalog.length]]);
    for (const p of catalog) counts.set(p.cat, (counts.get(p.cat) ?? 0) + 1);
    return [...counts.entries()];
  }, [catalog]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return catalog.filter(
      (p) => (cat === ALL || p.cat === cat) && (!q || p.name.toLowerCase().includes(q)),
    );
  }, [catalog, cat, query]);

  const total = cartTotal(cart);
  const sheetProduct = catalog.find((p) => p.id === sheetId) ?? null;

  return (
    <>
      <AppBar
        title="ເລືອກສິນຄ້າ"
        right={
          <button type="button" onClick={() => navigate('/add-product')}>
            <Text weight={700} size={13} color={colors.sky}>
              + ເພີ່ມ
            </Text>
          </button>
        }
      />

      <div className={styles.searchRow}>
        <IconSearch size={16} className={styles.searchIcon} />
        {/* The placeholder no longer offers "ຫຼື barcode": a product carries no
            barcode field anywhere in the codebase, so typing one never matched
            and the search simply came up empty. */}
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="ຄົ້ນຫາຊື່ສິນຄ້າ..."
          aria-label="ຄົ້ນຫາສິນຄ້າ"
          className={styles.searchInput}
        />
        {/* Barcodes only exist on boxed/bagged goods, so scanning is a shortcut,
            never the only way in. */}
        {scannable ? (
          <button
            type="button"
            onClick={() => post('scan.request')}
            className={styles.scanChip}
          >
            <Text weight={700} size={11} color={colors.sky}>
              ສະແກນ
            </Text>
          </button>
        ) : query ? (
          <button
            type="button"
            onClick={() => setQuery('')}
            className={styles.clearChip}
            aria-label="ລຶບຄຳຄົ້ນຫາ"
          >
            <IconClose size={14} />
          </button>
        ) : null}
      </div>

      <div className={cx('hscroll', styles.pills)} role="tablist">
        {categories.map(([name, count]) => (
          <CategoryPill
            key={name}
            name={name}
            icon={CATEGORY_ICONS[name] ?? 'grid'}
            count={count}
            selected={cat === name}
            onPress={() => setCat(name)}
          />
        ))}
      </div>

      <div className={cx('vscroll', styles.list)}>
        {visible.map((product) => (
          <ProductTile
            key={product.id}
            product={product}
            unitIndex={selectedUnit(product.id)}
            lines={linesOf(product.id)}
            image={images[product.id]}
            onPress={() => setSheetId(product.id)}
          />
        ))}

        <button
          type="button"
          onClick={() => navigate('/add-product')}
          className={cx(styles.dashed, styles.spanFull)}
        >
          <Text weight={700} size={13} color={colors.navy}>
            + ເພີ່ມສິນຄ້າໃໝ່
          </Text>
        </button>

        {/* Add-while-selling: an unknown item typed mid-sale becomes catalog data
            rather than a dead end, which is how the catalog fills up in practice. */}
        {visible.length === 0 && query.trim() ? (
          <Card className={cx(styles.notFound, styles.spanFull)}>
            <Text size={12.5} color={colors.textMuted}>
              ບໍ່ພົບ “{query.trim()}”
            </Text>
            <Text weight={700} size={13}>
              ບັນທຶກເປັນສິນຄ້າໃໝ່?
            </Text>
            <button
              type="button"
              onClick={() => navigate(`/add-product?name=${encodeURIComponent(query.trim())}`)}
              className={styles.notFoundCta}
            >
              <Text weight={800} size={13} color={colors.surfaceAlt}>
                ເພີ່ມ “{query.trim()}”
              </Text>
            </button>
          </Card>
        ) : null}
      </div>

      <QtySheet
        product={sheetProduct}
        unitIndex={sheetProduct ? selectedUnit(sheetProduct.id) : 0}
        qty={sheetProduct ? qtyOf(sheetProduct.id) : 0}
        onSelectUnit={(i) => sheetProduct && selectUnit(sheetProduct.id, i)}
        onConfirm={(next) => {
          if (sheetProduct) setQty(sheetProduct.id, next);
          setSheetId(null);
        }}
        onClose={() => setSheetId(null)}
      />

      <button
        type="button"
        disabled={cart.length === 0}
        onClick={() => navigate('/cart')}
        className={styles.cartBar}
      >
        <Mono weight={700} size={13} color={colors.surfaceAlt}>
          {cart.length} ລາຍການ · {fmt(total)} ₭
        </Mono>
        <Text weight={800} size={14} color={colors.surfaceAlt}>
          ໄປຕະກ້າ →
        </Text>
      </button>
    </>
  );
}

/* The full-width list and the toggle that chose between it and the grid are
   gone. Two ways to render one catalogue meant every change to a product's
   presentation had to be made twice, and the grid — now that it carries the
   picture — is the one that answers "which bag is that" without reading. */

/**
 * Grid tile: picture, name, price — the shape every POS grid has, Odoo's
 * included, because a shopkeeper finds a bag of cement by its label faster than
 * by reading it.
 *
 * The picture is optional and always will be. A shop of three hundred products
 * is not going to photograph all of them, and a grid where half the tiles are
 * grey boxes is worse than one with none — so a product without a picture gets
 * its category's glyph on a tinted panel, which is a deliberate-looking tile
 * rather than a hole where an image failed.
 *
 * No stepper: at half width the quantity sheet is the only sane way in.
 */
function ProductTile({
  product,
  unitIndex,
  lines,
  image,
  onPress,
}: {
  product: Product;
  unitIndex: number;
  lines: CartLine[];
  image?: string;
  onPress: () => void;
}) {
  const unit = product.units[unitIndex] ?? product.units[0]!;
  const inCart = lines.length > 0;

  return (
    <button
      type="button"
      aria-label={`${product.name} · ໃສ່ຈຳນວນ`}
      onClick={onPress}
      className={styles.tileWrap}
    >
      <Card className={cx(styles.tile, inCart && styles.rowOn)}>
        <div className={styles.tileImage}>
          {image ? (
            // `object-fit: contain` in the stylesheet: a bag of cement cropped
            // to a square by `cover` can lose the brand, which is the whole
            // reason the picture is there.
            <img src={image} alt="" className={styles.tilePhoto} />
          ) : (
            <CategoryGlyph
              icon={CATEGORY_ICONS[product.cat] ?? 'grid'}
              size={30}
              className={styles.tileGlyph}
            />
          )}

          {inCart ? (
            <span className={styles.tileBadge}>
              <Mono weight={700} size={11} color={colors.navy}>
                {summariseLines(lines)}
              </Mono>
            </span>
          ) : null}
        </div>

        {/* Two lines: half width is not enough for most Lao product names. */}
        <Text weight={700} size={13} lines={2} align="left" className={styles.tileName}>
          {product.name}
        </Text>
        <Mono weight={700} size={12} color={colors.navy} align="left" className={styles.tilePrice}>
          {fmt(unit.price)} ₭
        </Mono>
        <Mono size={9.5} color={colors.textFaint} align="left" className={styles.tilePrice}>
          / {unit.unit}
          {product.units.length > 1 ? ` · ${product.units.length} ໜ່ວຍ` : ''}
        </Mono>
      </Card>
    </button>
  );
}

/** "12 ຖົງ" — or "12 ຖົງ · 0.5 ໂຕນ" when the same product was sold by two units. */
function summariseLines(lines: CartLine[]): string {
  return lines.map((l) => `${fmtQty(l.qty)} ${l.unit}`).join(' · ');
}
